import { toSeconds } from '@art-suite/art-core-ts';
import type { ApiScanNote, FlattenedApiScanNote } from '@kaiizen/kai-pro-api';
import type { ScanRootOptimized } from '@kaiizen/kai-scan-template-system';

import { getGroupTitle, getTaskTitle } from './scopeNavigatorPartPresentersLib';

/**
 * Notes across the scope, at the three levels a note can be filed at and read from.
 *
 * One source for both the notes drawer and the editor's in-page notes section, so a count shown
 * anywhere is always the length of a list rendered from the same entries.
 */

export type ScopeNotesLevel =
  | { kind: 'scope' }
  | { kind: 'group'; groupId: string }
  | { kind: 'task'; groupId: string; taskId: string };

export type ScopeNoteOriginKind = ScopeNotesLevel['kind'];

/** Where a note was filed — and the label it carries when read from a level above it. */
export type ScopeNoteOrigin = {
  kind: ScopeNoteOriginKind;
  groupId?: string;
  taskId?: string;
  /** "Scope", "Kitchen", or "Kitchen › Cabinets". */
  label: string;
};

export type ScopeNoteEntry = { note: FlattenedApiScanNote; origin: ScopeNoteOrigin };

export const SCOPE_NOTE_ORIGIN_LABEL = 'Scope';
export const SCOPE_NOTE_ORIGIN_SEPARATOR = ' › ';

/**
 * Every scan part a note can hang off, mapped to the level it reads at. A task's options and line
 * items are the task's own business — a note on one of them is a task note, labelled with the task.
 */
export const buildScopeNoteOriginIndex = (scanRoot: ScanRootOptimized): Map<string, ScopeNoteOrigin> => {
  const index = new Map<string, ScopeNoteOrigin>();
  index.set(scanRoot.id, { kind: 'scope', label: SCOPE_NOTE_ORIGIN_LABEL });
  for (const group of scanRoot.groups ?? []) {
    const groupTitle = getGroupTitle(group);
    index.set(group.id, { kind: 'group', groupId: group.id, label: groupTitle });
    for (const task of group.tasks ?? []) {
      const origin: ScopeNoteOrigin = {
        kind: 'task',
        groupId: group.id,
        taskId: task.id,
        label: `${groupTitle}${SCOPE_NOTE_ORIGIN_SEPARATOR}${getTaskTitle(task)}`,
      };
      index.set(task.id, origin);
      for (const option of task.options ?? []) {
        index.set(option.id, origin);
        for (const lineItem of option.lineItems ?? []) index.set(lineItem.id, origin);
      }
    }
  }
  return index;
};

const originIsWithinLevel = (origin: ScopeNoteOrigin, level: ScopeNotesLevel): boolean => {
  switch (level.kind) {
    case 'scope':
      return true;
    case 'group':
      return origin.groupId === level.groupId;
    case 'task':
      return origin.taskId === level.taskId;
  }
};

/**
 * The notes a level reads, newest first across every source:
 * - task: that task's own notes;
 * - group: the group's own notes plus every note from its tasks;
 * - scope: everything.
 *
 * Expects already-flattened notes (edits applied, deletes resolved away — see
 * `getFlattenedScanNotes`), keyed by scan part. Notes with nothing written in them (a bare approval
 * carries a status, not a remark) and notes on parts no longer in the scope are left out: there is
 * nothing to read in the first, and nowhere to say the second came from.
 */
export const getScopeNotesForLevel = (
  scanRoot: ScanRootOptimized,
  scanNotesByScanPartId: Record<string, ApiScanNote[]>,
  level: ScopeNotesLevel,
  originIndex: Map<string, ScopeNoteOrigin> = buildScopeNoteOriginIndex(scanRoot),
): ScopeNoteEntry[] => {
  const entries: ScopeNoteEntry[] = [];
  for (const [scanPartId, notes] of Object.entries(scanNotesByScanPartId)) {
    const origin = originIndex.get(scanPartId);
    if (!origin || !originIsWithinLevel(origin, level)) continue;
    for (const note of notes) {
      if (note.body?.trim()) entries.push({ note, origin });
    }
  }
  return entries.sort((left, right) => toSeconds(right.note.createdAt) - toSeconds(left.note.createdAt));
};

export type ScopeNoteCounts = Record<ScopeNoteOriginKind, number>;

export const countScopeNotesByOrigin = (entries: readonly ScopeNoteEntry[]): ScopeNoteCounts => {
  const counts: ScopeNoteCounts = { scope: 0, group: 0, task: 0 };
  for (const { origin } of entries) counts[origin.kind] += 1;
  return counts;
};

const pluralize = (count: number, singular: string, plural: string) => `${count} ${count === 1 ? singular : plural}`;

/**
 * A level header's count, split by where the notes came from — "2 groups · 0 tasks". Task headers
 * carry none: a task's list is all its own. The scope's own notes only get a segment once there
 * are any, so the segments always add up to the list below.
 */
export const formatScopeNotesCount = (level: ScopeNotesLevel, counts: ScopeNoteCounts): string | undefined => {
  if (level.kind === 'task') return undefined;
  const segments = [
    ...(level.kind === 'scope' && counts.scope > 0 ? [pluralize(counts.scope, 'scope', 'scope')] : []),
    pluralize(counts.group, 'group', 'groups'),
    pluralize(counts.task, 'task', 'tasks'),
  ];
  return segments.join(' · ');
};
